import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { VeteranBanner } from "../components/VeteranBanner";
import { PricingCalculator } from "../components/PricingCalculator";
import { Button } from "../components/ui/button";
import {
  Sun, Droplets, ShieldCheck, Zap, ArrowRight, Star, CalendarCheck,
  SprayCan, ClipboardCheck, Award,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1754619880959-2b0528375883?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHx2ZXRlcmFuJTIwd29ya2VyJTIwc29sYXIlMjBjbGVhbmluZ3xlbnwwfHx8fDE3OTAxNjU5ODV8MA&ixlib=rb-4.1.0&q=85";
const CREW = "https://images.pexels.com/photos/14614266/pexels-photo-14614266.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HONOR = "https://images.pexels.com/photos/7984340/pexels-photo-7984340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DETAIL = "https://images.unsplash.com/photo-1719848576338-9516ba7ccd8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHx2ZXRlcmFuJTIwd29ya2VyJTIwc29sYXIlMjBjbGVhbmluZ3xlbnwwfHx8fDE3OTAxNjU5ODV8MA&ixlib=rb-4.1.0&q=85";

const STEPS = [
  { icon: ClipboardCheck, t: "1. Get Your Quote", d: "Use our live calculator to size the job and see restored output instantly." },
  { icon: CalendarCheck, t: "2. Pick a Slot", d: "Choose a date and a morning or afternoon arrival window that suits you." },
  { icon: SprayCan, t: "3. We Clean", d: "Our vetted crew arrives with pure deionized water for a streak-free finish." },
  { icon: Zap, t: "4. Enjoy More Power", d: "Watch your panels recover 15–30% of lost output and lower your bills." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div>
      <VeteranBanner />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-16 sm:py-24">
          <div className="reveal">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Veteran-Owned & Operated
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mt-6 leading-[1.05]">
              Solar panels cleaned with <span className="text-emerald-700">military precision.</span>
            </h1>
            <p className="text-lg text-slate-600 mt-6 max-w-xl leading-relaxed">
              Dirty panels can quietly steal up to 30% of your energy output. We restore it with pure deionized water and a streak-free guarantee — booked, managed, and paid online in minutes.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <Button onClick={() => navigate("/book")} data-testid="hero-book-btn"
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-13 px-7 text-base font-bold gap-2 shadow-lg h-12">
                Book a Cleaning <ArrowRight className="w-4 h-4" />
              </Button>
              <a href="#pricing" data-testid="hero-quote-link"
                className="text-slate-700 font-semibold hover:text-emerald-700 transition-colors inline-flex items-center gap-2">
                Estimate my savings <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="flex items-center gap-6 mt-10">
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                <span className="text-sm font-bold text-slate-900 ml-1">4.9</span>
                <span className="text-sm text-slate-500">/ 1,200+ jobs</span>
              </div>
              <div className="h-6 w-px bg-slate-200" />
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Droplets className="w-4 h-4 text-emerald-600" /> Pure-water · streak-free
              </div>
            </div>
          </div>

          <div className="relative reveal" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
              <img src={HERO} alt="Technician cleaning solar panels" className="w-full h-[380px] sm:h-[460px] object-cover" data-testid="hero-image" />
            </div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl border border-slate-100 p-5 w-52">
              <div className="flex items-center gap-2 text-emerald-700 font-black text-3xl font-mono"><Zap className="w-6 h-6 text-amber-500" />+27%</div>
              <div className="text-sm text-slate-600 mt-1 font-medium">avg. output restored after a clean</div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[["1,200+", "Roofs cleaned"], ["4.9★", "Customer rating"], ["100%", "Veteran-owned"], ["+27%", "Avg output gain"]].map(([n, l]) => (
            <div key={l}>
              <div className="text-3xl sm:text-4xl font-black font-mono text-amber-400">{n}</div>
              <div className="text-sm text-slate-400 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200">How it works</span>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mt-5">From dusty to dialed-in, in four steps</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {STEPS.map((s, i) => (
            <div key={s.t} className="bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 transition-all" data-testid={`step-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-emerald-700 flex items-center justify-center mb-4"><s.icon className="w-5 h-5 text-white" /></div>
              <h3 className="font-heading font-bold text-slate-900 text-lg">{s.t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING CALCULATOR */}
      <section id="pricing" className="bg-emerald-50/60 border-y border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 bg-white px-3 py-1.5 rounded-full border border-emerald-200">Transparent pricing</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mt-5">Build your quote &amp; see the payoff</h2>
            <p className="text-slate-600 mt-3">Slide, tap, and toggle. No hidden fees — the price you see is the price you pay.</p>
          </div>
          <PricingCalculator />
        </div>
      </section>

      {/* VETERAN STORY */}
      <section id="story" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src={HONOR} alt="Veteran honor" className="rounded-2xl object-cover h-64 w-full shadow-lg" />
            <img src={DETAIL} alt="Solar detail" className="rounded-2xl object-cover h-64 w-full shadow-lg mt-8" />
          </div>
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Our Story</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 mt-5">Founded on discipline. Built for your roof.</h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Stars Cleaning was founded by <span className="font-semibold text-slate-900">SRA Johnathan Mingo, USAF</span>, who brought the same rigor from the field to solar maintenance. Every crew member is trained to treat your panels like mission-critical gear — no shortcuts, no streaks, no damage.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {[["Streak-free guarantee", "100% pure deionized water — or we re-clean free."], ["15% Veteran discount", "For vets & first responders on their first cleaning."]].map(([t, d]) => (
                <div key={t} className="flex gap-3 bg-white rounded-xl border border-slate-200 p-4">
                  <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  <div><div className="font-bold text-slate-900 text-sm">{t}</div><div className="text-sm text-slate-600 mt-0.5">{d}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="relative rounded-3xl overflow-hidden bg-slate-900 grain">
          <img src={CREW} alt="Crew on roof" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="relative px-8 sm:px-16 py-16 sm:py-20 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-white max-w-2xl mx-auto leading-tight">Ready to reclaim your solar output?</h2>
            <p className="text-slate-300 mt-4 max-w-xl mx-auto">Book online in under two minutes. Manage, reschedule, and pay — all in one place.</p>
            <Button onClick={() => navigate("/book")} data-testid="cta-book-btn" className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold h-12 px-8 mt-8 gap-2">
              Book a Cleaning <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
